/**
 * Detail modal del Action Center Premium (0668+).
 *
 * Modal centrado con backdrop semitransparente · estructura seccionada
 * premium siguiendo el patrón de Personas + nivel 3 mapa de procesos +
 * "Solicita y Aprueba" Grupo Puentes:
 *
 *   Header  · tipo (badge) + asunto BOLD + estado chip + close X
 *   Sección · DATOS (asunto · alcance · código · solicitante · fecha · estado)
 *   Sección · FLUJO DE APROBACIÓN (timeline horizontal etapas)
 *   Sección · NOTIFICADOS (audit trail WorkflowNotifEnvio ADR-027.1)
 *   Sección · ADJUNTOS (placeholder cross-artefacto)
 *   Footer  · Delegar (outline) · textarea comentario · Cancelar/Rechazar/Aprobar
 *
 * Solo se monta cuando hay item seleccionado y es de motor unificado
 * (ORG/DOC/FLUJOGRAMA). Para legacy_sign no se muestra detail (footer
 * desactivado y se ofrece urlAccion como fallback).
 */

import { useState } from 'react';
import { useI18n } from '../../../../i18n';
import type { ActionItem } from '../hooks/useActionCenterList';
import {
  tipoAtipoArtefacto,
  useActionCenterDetail,
  type ArtefactoWorkflowTipo,
} from '../hooks/useActionCenterDetail';
import { useActionCenterMutate } from '../hooks/useActionCenterMutate';
import { DelegarModal } from './DelegarModal';
import { Markdown } from './Markdown';
import { AdjuntosSection } from './AdjuntosSection';
import { ComentariosSection } from './ComentariosSection';

export interface ActionCenterDetailPanelProps {
  item: ActionItem;
  onClose: () => void;
}

const TIPO_BADGE_BG: Record<string, string> = {
  ORGANIGRAMA: '#E0F2FE',
  DOC: '#FCE7F3',
  FLUJOGRAMA: '#FEF3C7',
  APROBACION_DOCUMENTO: '#E5E7EB',
  APROBACION_PETICION: '#D1FAE5',
  APROBACION_COMPRA: '#FEE2E2',
};
const TIPO_BADGE_FG: Record<string, string> = {
  ORGANIGRAMA: '#075985',
  DOC: '#9D174D',
  FLUJOGRAMA: '#92400E',
  APROBACION_DOCUMENTO: '#374151',
  APROBACION_PETICION: '#065F46',
  APROBACION_COMPRA: '#991B1B',
};

export function ActionCenterDetailPanel(props: ActionCenterDetailPanelProps) {
  const { t } = useI18n();
  const tt = t.sign.actionCenter.premium;

  const tipo: ArtefactoWorkflowTipo | null = tipoAtipoArtefacto(props.item.tipo);
  const isMotorUnificado = tipo !== null;

  const { detail, timeline, isLoading, isError } = useActionCenterDetail(
    tipo,
    props.item.id,
    isMotorUnificado,
  );
  const mutation = useActionCenterMutate();

  const [observaciones, setObservaciones] = useState('');
  const [rejectMode, setRejectMode] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [delegarOpen, setDelegarOpen] = useState(false);

  const handleApprove = async () => {
    if (!isMotorUnificado || !tipo) return;
    setErrorMsg(null);
    try {
      await mutation.mutateAsync({
        tipo,
        id: props.item.id,
        decision: 'APROBAR',
        observaciones: observaciones.trim() || undefined,
      });
      props.onClose();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : tt.approveError);
    }
  };

  const handleReject = async () => {
    if (!isMotorUnificado || !tipo) return;
    if (motivo.trim().length < 5) {
      setErrorMsg(tt.rejectMotivoLabel);
      return;
    }
    setErrorMsg(null);
    try {
      await mutation.mutateAsync({
        tipo,
        id: props.item.id,
        decision: 'RECHAZAR',
        motivo: motivo.trim(),
        observaciones: observaciones.trim() || undefined,
      });
      props.onClose();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : tt.rejectError);
    }
  };

  const tipoBadgeBg = TIPO_BADGE_BG[props.item.tipo] ?? '#E5E7EB';
  const tipoBadgeFg = TIPO_BADGE_FG[props.item.tipo] ?? '#374151';
  const asuntoVisible = detail?.artefacto.asunto ?? props.item.asunto ?? props.item.titulo;
  const alcanceVisible = detail?.artefacto.alcance ?? props.item.alcance;
  const codigoVisible = detail?.artefacto.codigo ?? props.item.codigo;
  const estadoVisible = detail?.artefacto.estado ?? props.item.estado ?? '';
  const estadoLabel =
    typeof estadoVisible === 'string' && estadoVisible ? estadoVisible.replace(/_/g, ' ') : '—';

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={asuntoVisible}
        onClick={(e) => {
          // Click en backdrop cierra el modal (fuera de la card).
          if (e.target === e.currentTarget) props.onClose();
        }}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          padding: 24,
          fontFamily: 'DM Sans, sans-serif',
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
            width: '100%',
            maxWidth: 920,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* HEADER del modal */}
          <header
            style={{
              padding: '20px 28px 16px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 16,
            }}
          >
            <span
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11,
                fontWeight: 700,
                padding: '4px 8px',
                background: tipoBadgeBg,
                color: tipoBadgeFg,
                borderRadius: 4,
                letterSpacing: 0.6,
                flex: '0 0 auto',
                marginTop: 4,
              }}
            >
              {labelTipo(props.item.tipo)}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#1F3864',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {asuntoVisible}
              </h2>
              {codigoVisible && (
                <code
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 11,
                    color: '#9CA3AF',
                    marginTop: 4,
                    display: 'inline-block',
                  }}
                >
                  {codigoVisible}
                </code>
              )}
            </div>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '4px 10px',
                background: '#F3F4F6',
                border: '1px solid #E5E7EB',
                color: '#374151',
                borderRadius: 12,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                flex: '0 0 auto',
                alignSelf: 'flex-start',
                marginTop: 4,
              }}
            >
              {estadoLabel}
            </span>
            {props.item.urlAccion && (
              <a
                href={props.item.urlAccion}
                target="_blank"
                rel="noopener noreferrer"
                title={openLabel(tt, props.item.tipo)}
                aria-label={openLabel(tt, props.item.tipo)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#1F3864',
                  textDecoration: 'none',
                  border: '1px solid #1F3864',
                  borderRadius: 6,
                  flex: '0 0 auto',
                  alignSelf: 'flex-start',
                  marginTop: 4,
                  background: 'transparent',
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M9 2h5v5" />
                  <path d="M14 2L7 9" />
                  <path d="M14 9v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h4" />
                </svg>
                {openLabel(tt, props.item.tipo)}
              </a>
            )}
            <button
              type="button"
              onClick={props.onClose}
              aria-label={tt.actionCancel}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#9CA3AF',
                fontSize: 24,
                lineHeight: 1,
                cursor: 'pointer',
                padding: 0,
                marginTop: 0,
                flex: '0 0 auto',
              }}
            >
              ×
            </button>
          </header>

          {/* BODY scrollable */}
          <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px' }}>
            {isLoading && <p style={{ color: '#9CA3AF', fontSize: 13, margin: 0 }}>{tt.loading}</p>}
            {isError && (
              <p
                style={{
                  color: '#991B1B',
                  fontSize: 13,
                  margin: '0 0 16px 0',
                  padding: 12,
                  background: '#FEF2F2',
                  border: '1px solid #FCA5A5',
                  borderRadius: 6,
                }}
                role="alert"
              >
                {tt.detailLoadError}
              </p>
            )}

            {/* SECCIÓN — Datos · alcance con render MD (0669) */}
            <Section title={tt.detailDetails}>
              <Grid2>
                {alcanceVisible && (
                  <Field label={tt.detailAlcance} fullWidth>
                    <Markdown source={alcanceVisible} />
                  </Field>
                )}
                <Field label={tt.detailSolicitante}>
                  <span style={{ color: '#1F2937', fontSize: 13 }}>
                    {props.item.solicitante.nombre ?? '—'}
                  </span>
                </Field>
                <Field label={tt.detailFechaSolicitud}>
                  <span style={{ color: '#1F2937', fontSize: 13 }}>
                    {formatFechaHora(props.item.fechaSolicitud)}
                  </span>
                </Field>
              </Grid2>
            </Section>

            {/* SECCIÓN — Flujo de aprobación · cards enriquecidas (0669) */}
            <Section title={tt.detailFlow}>
              {timeline && Array.isArray(timeline.etapas) && timeline.etapas.length > 0 ? (
                <ol
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'grid',
                    gridTemplateColumns: `repeat(${Math.min(timeline.etapas.length, 4)}, 1fr)`,
                    gap: 10,
                  }}
                >
                  {timeline.etapas.map((etapa) => (
                    <FlowCard key={`${etapa.orden}-${etapa.accion}`} etapa={etapa} tt={tt} />
                  ))}
                </ol>
              ) : (
                <p style={{ color: '#9CA3AF', fontSize: 13, margin: 0 }}>—</p>
              )}
            </Section>

            {/* SECCIÓN — Notificados · colapsable cerrado por defecto (0669) */}
            <section style={{ marginBottom: 24 }}>
              {detail && detail.notificados.length > 0 ? (
                <details style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 0 }}>
                  <summary
                    style={{
                      cursor: 'pointer',
                      padding: '10px 14px',
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#1F3864',
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      listStyle: 'none',
                    }}
                  >
                    <span style={{ flex: '0 0 auto', fontSize: 14 }}>›</span>
                    {tt.notifiedToggleShow.replace('{n}', String(detail.notificados.length))}
                  </summary>
                  <ul style={{ listStyle: 'none', padding: '0 14px 14px', margin: 0 }}>
                    {detail.notificados.map((n) => (
                      <li
                        key={n.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto auto auto',
                          alignItems: 'center',
                          gap: 12,
                          padding: '10px 0',
                          borderBottom: '1px solid #F3F4F6',
                          fontSize: 13,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <strong
                            style={{
                              color: '#1F2937',
                              display: 'block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {n.destinatarioNombre ?? n.destinatarioEmail ?? '—'}
                          </strong>
                          <div
                            style={{
                              display: 'flex',
                              gap: 10,
                              fontSize: 11,
                              color: '#6B7280',
                              marginTop: 2,
                            }}
                          >
                            {n.rolGobernanza && (
                              <span>
                                <span style={{ color: '#9CA3AF', marginRight: 4 }}>
                                  {tt.notifiedRol}:
                                </span>
                                {n.rolGobernanza}
                              </span>
                            )}
                            {n.puesto && (
                              <span>
                                <span style={{ color: '#9CA3AF', marginRight: 4 }}>
                                  {tt.notifiedPuesto}:
                                </span>
                                {n.puesto.titulo}
                                {n.puesto.codigo && (
                                  <span
                                    style={{
                                      fontFamily: 'JetBrains Mono, monospace',
                                      color: '#9CA3AF',
                                      marginLeft: 4,
                                    }}
                                  >
                                    · {n.puesto.codigo}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: 11,
                            fontFamily: 'JetBrains Mono, monospace',
                            color: '#6B7280',
                            padding: '2px 6px',
                            background: '#F3F4F6',
                            borderRadius: 3,
                          }}
                        >
                          {n.canal}
                        </span>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 500,
                            color: n.status === 'SENT' ? '#065F46' : '#991B1B',
                          }}
                        >
                          {n.status}
                        </span>
                        <span style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          {formatFechaHora(n.enviadoEn)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : (
                <h3
                  style={{
                    margin: '0 0 12px 0',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#9CA3AF',
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                  }}
                >
                  {tt.detailNotifiedEmpty}
                </h3>
              )}
            </section>

            {/* SECCIÓN — Comentarios (0675 F3 piloto #2 · cross-artefacto motor unificado) */}
            <Section title={tt.comentarios.title}>
              {isMotorUnificado && tipo ? (
                <ComentariosSection artefactoTipo={tipo} artefactoId={props.item.id} />
              ) : (
                <p style={{ color: '#9CA3AF', fontSize: 13, margin: 0 }}>
                  {/* Fix LOW-1 Codex 0675: usar texto propio de comentarios (antes mostraba el de adjuntos) */}
                  {tt.comentarios.empty}
                </p>
              )}
            </Section>

            {/* SECCIÓN — Adjuntos (0674 F3 piloto · cross-artefacto motor unificado) */}
            <Section title={tt.detailAttachmentsTitle}>
              {isMotorUnificado && tipo ? (
                <AdjuntosSection artefactoTipo={tipo} artefactoId={props.item.id} />
              ) : (
                <p style={{ color: '#9CA3AF', fontSize: 13, margin: 0 }}>
                  {tt.detailAttachmentsEmpty}
                </p>
              )}
            </Section>
          </div>

          {/* FOOTER fijo */}
          <footer
            style={{
              padding: '12px 28px 20px',
              borderTop: '1px solid #E5E7EB',
              background: '#FAFAFA',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <textarea
              value={rejectMode ? motivo : observaciones}
              onChange={(e) =>
                rejectMode ? setMotivo(e.target.value) : setObservaciones(e.target.value)
              }
              placeholder={rejectMode ? tt.rejectMotivoPlaceholder : tt.observationsPlaceholder}
              aria-label={rejectMode ? tt.rejectMotivoLabel : tt.observationsLabel}
              rows={2}
              style={{
                width: '100%',
                padding: 10,
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 13,
                border: rejectMode ? '1px solid #DC2626' : '1px solid #D1D5DB',
                borderRadius: 6,
                resize: 'vertical',
                outline: 'none',
                background: '#fff',
                boxSizing: 'border-box',
              }}
            />
            {errorMsg && (
              <span style={{ color: '#991B1B', fontSize: 12 }} role="alert">
                {errorMsg}
              </span>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setDelegarOpen(true)}
                disabled={!isMotorUnificado || mutation.isPending || rejectMode}
                style={btnOutline}
              >
                {tt.actionDelegate}
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    if (rejectMode) {
                      setRejectMode(false);
                      setMotivo('');
                    } else {
                      props.onClose();
                    }
                  }}
                  style={btnGrey}
                >
                  {tt.actionCancel}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!rejectMode) setRejectMode(true);
                    else handleReject();
                  }}
                  disabled={!isMotorUnificado || mutation.isPending}
                  style={btnRed}
                >
                  {mutation.isPending && rejectMode ? tt.actionRejecting : tt.actionReject}
                </button>
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={!isMotorUnificado || mutation.isPending || rejectMode}
                  style={btnGreen}
                >
                  {mutation.isPending && !rejectMode ? tt.actionApproving : tt.actionApprove}
                </button>
              </div>
            </div>
          </footer>
        </div>
      </div>

      {delegarOpen && tipo && (
        <DelegarModal
          tipo={tipo}
          id={props.item.id}
          artefactoLabel={asuntoVisible}
          onClose={() => setDelegarOpen(false)}
          onDelegado={() => {
            setDelegarOpen(false);
            props.onClose();
          }}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Subcomponentes auxiliares (privados al modal · no se exportan)
// ────────────────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 24 }}>
      <h3
        style={{
          margin: '0 0 12px 0',
          fontSize: 11,
          fontWeight: 700,
          color: '#1F3864',
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function Grid2({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '12px 24px',
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  fullWidth,
  children,
}: {
  label: string;
  fullWidth?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : 'auto' }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: '#9CA3AF',
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function openLabel(
  tt: {
    actionOpen: string;
    actionOpenOrganigrama: string;
    actionOpenDocumento: string;
    actionOpenFlujograma: string;
  },
  tipo: string,
): string {
  if (tipo === 'ORGANIGRAMA') return tt.actionOpenOrganigrama;
  if (tipo === 'DOC' || tipo === 'APROBACION_DOCUMENTO') return tt.actionOpenDocumento;
  if (tipo === 'FLUJOGRAMA') return tt.actionOpenFlujograma;
  return tt.actionOpen;
}

function labelTipo(tipo: string): string {
  if (tipo === 'ORGANIGRAMA') return 'ORG';
  if (tipo === 'DOC') return 'DOC';
  if (tipo === 'FLUJOGRAMA') return 'FLW';
  if (tipo === 'APROBACION_DOCUMENTO') return 'DOC*';
  if (tipo === 'APROBACION_PETICION') return 'PET';
  if (tipo === 'APROBACION_COMPRA') return 'COMP';
  return tipo;
}

function formatFechaHora(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

// SVG sobre (envelope) para el popover de comentario per-etapa (0669).
const SVG_ENVELOPE = (
  <svg
    width="13"
    height="13"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="2" y="3.5" width="12" height="9" rx="1" />
    <path d="M2.5 4.5l5.5 4 5.5-4" />
  </svg>
);

// 0669 — Card de etapa enriquecida con fecha + rol + puesto + popover sobre
// con comentario/motivo. Si la etapa no tiene motivo, el sobre se renderiza
// en gris desactivado pero accesible para el screen reader.
function FlowCard({
  etapa,
  tt,
}: {
  etapa: import('../hooks/useActionCenterDetail').WorkflowTimelineEtapa;
  tt: {
    flowFecha: string;
    flowRol: string;
    flowPuesto: string;
    flowMotivo: string;
    flowMotivoEmpty: string;
    flowMotivoTitle: string;
  };
}) {
  const [hover, setHover] = useState(false);
  const bg =
    etapa.estado === 'DONE' ? '#10B981' : etapa.estado === 'PENDING' ? '#3B82F6' : '#E5E7EB';
  const fg = etapa.estado === 'FUTURE' || etapa.estado === 'SKIPPED' ? '#6B7280' : '#fff';
  const actorNombre = etapa.actor?.nombreCompleto ?? null;
  const rolLabel = etapa.rolEsperado ?? null;
  const puesto = etapa.actor?.puesto ?? null;
  const fechaIso = etapa.actor?.fecha ?? null;
  const motivo = etapa.motivo;
  const decisionLabel = etapa.decision ? ` · ${etapa.decision}` : '';

  return (
    <li
      style={{
        position: 'relative',
        padding: 12,
        background: '#F9FAFB',
        borderRadius: 8,
        border: '1px solid #E5E7EB',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: bg,
            color: fg,
            fontSize: 11,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto',
          }}
        >
          {etapa.estado === 'DONE' ? '✓' : etapa.posicion}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#1F3864',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          {etapa.accion}
        </span>
        <span
          style={{
            fontSize: 10,
            fontFamily: 'JetBrains Mono, monospace',
            color: '#9CA3AF',
          }}
        >
          {etapa.posicion}/{etapa.totalEtapas}
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          aria-label={tt.flowMotivoTitle}
          title={tt.flowMotivoTitle}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setHover(true)}
          onBlur={() => setHover(false)}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: motivo ? 'pointer' : 'default',
            color: motivo ? '#1F3864' : '#D1D5DB',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          {SVG_ENVELOPE}
        </button>
      </div>

      {/* Nombre actor + decisión */}
      <div
        style={{
          fontSize: 12,
          color: '#1F2937',
          fontWeight: 600,
          lineHeight: 1.3,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {actorNombre ? `${actorNombre}${decisionLabel}` : '—'}
      </div>

      {/* Metadatos rol + puesto + fecha */}
      <div
        style={{
          fontSize: 11,
          color: '#6B7280',
          lineHeight: 1.45,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {rolLabel && (
          <span>
            <span style={{ color: '#9CA3AF', marginRight: 4 }}>{tt.flowRol}:</span>
            {rolLabel}
          </span>
        )}
        {puesto && (
          <span>
            <span style={{ color: '#9CA3AF', marginRight: 4 }}>{tt.flowPuesto}:</span>
            {puesto.titulo}
            {puesto.codigo && (
              <span
                style={{ fontFamily: 'JetBrains Mono, monospace', marginLeft: 4, color: '#9CA3AF' }}
              >
                · {puesto.codigo}
              </span>
            )}
          </span>
        )}
        {fechaIso && (
          <span>
            <span style={{ color: '#9CA3AF', marginRight: 4 }}>{tt.flowFecha}:</span>
            {formatFechaHora(fechaIso)}
          </span>
        )}
      </div>

      {/* Popover comentario (hover/focus sobre el sobre) */}
      {hover && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            transform: 'translateY(-100%)',
            background: '#1F3864',
            color: '#fff',
            fontSize: 12,
            lineHeight: 1.4,
            padding: '8px 10px',
            borderRadius: 6,
            maxWidth: 280,
            minWidth: 180,
            boxShadow: '0 6px 18px rgba(15,23,42,0.25)',
            zIndex: 60,
            whiteSpace: 'pre-wrap',
            pointerEvents: 'none',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 11, letterSpacing: 0.4 }}>
            {tt.flowMotivo}
          </div>
          <div style={{ color: motivo ? '#fff' : '#D1D5DB' }}>{motivo ?? tt.flowMotivoEmpty}</div>
        </div>
      )}
    </li>
  );
}

const btnBase: React.CSSProperties = {
  padding: '8px 18px',
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 13,
  fontWeight: 600,
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
};
const btnGrey: React.CSSProperties = { ...btnBase, color: '#374151', background: '#E5E7EB' };
const btnRed: React.CSSProperties = { ...btnBase, color: '#fff', background: '#DC2626' };
const btnGreen: React.CSSProperties = { ...btnBase, color: '#fff', background: '#10B981' };
const btnOutline: React.CSSProperties = {
  ...btnBase,
  color: '#1F3864',
  background: 'transparent',
  border: '1px solid #1F3864',
};
